import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  ArrowLeft, MapPin, User, Phone, Mail, Plane, Calendar, Clock,
  Car, DollarSign, CreditCard, CheckCircle, XCircle, Truck, Copy,
  Navigation, Send, Loader2, Star, MailCheck, Wallet, Edit2, Save, X, FileText, Banknote
} from "lucide-react";
import { DriverLocationTracker } from "../components/DriverTracking";
import { RatingSubmitForm } from "./RatingsPage";

const BookingDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [sendingTracking, setSendingTracking] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [existingRating, setExistingRating] = useState(null);
  const [showRatingForm, setShowRatingForm] = useState(false);
  
  // Driver pay state
  const [driverPay, setDriverPay] = useState("");
  const [savingDriverPay, setSavingDriverPay] = useState(false);
  
  // Price editing state (admin only)
  const [editingPrice, setEditingPrice] = useState(false);
  const [newPrice, setNewPrice] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);
  
  // Edit booking state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Mark as paid state
  const [markingPaid, setMarkingPaid] = useState(false);
  
  // Create invoice state
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  // Fetch vehicle types for edit dropdown
  const fetchVehicleTypes = async () => {
    try {
      const response = await api.get("/vehicles");
      setVehicleTypes(response.data.filter(v => v.is_active));
    } catch (error) {
      console.error("Failed to load vehicle types");
    }
  };

  const checkExistingRating = async () => {
    try {
      const response = await api.get(`/ratings/booking/${id}`);
      setExistingRating(response.data);
    } catch {
      // No rating exists, allow user to rate
      setExistingRating(null);
    }
  };

  const fetchBooking = async () => {
    try {
      console.log(`Fetching booking with ID: ${id}`);
      const token = localStorage.getItem("token");
      console.log(`Token present: ${!!token}`);
      
      const response = await api.get(`/bookings/${id}`);
      console.log(`Booking fetched successfully:`, response.data.booking_ref);
      setBooking(response.data);
      // Initialize driver pay from booking - handle null/undefined safely
      const driverPayValue = response.data.driver_pay;
      setDriverPay(driverPayValue != null ? String(driverPayValue) : "");
    } catch (error) {
      console.error("Booking fetch error:", error.response?.status, error.message);
      if (error.response?.status === 401) {
        // Token expired or invalid - handled by interceptor
        return;
      }
      toast.error("Booking not found");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await api.get("/users/drivers");
      setDrivers(response.data);
    } catch (error) {
      console.error("Failed to load drivers");
    }
  };

  const fetchPaymentQuote = async () => {
    try {
      const response = await api.post("/payments/quote", { booking_id: id });
      setPaymentQuote(response.data);
    } catch (error) {
      console.error("Failed to fetch payment quote");
    }
  };

  const handleSaveDriverPay = async () => {
    if (!driverPay || isNaN(parseFloat(driverPay))) {
      toast.error("Please enter a valid driver pay amount");
      return;
    }
    
    setSavingDriverPay(true);
    try {
      await api.put(`/bookings/${id}/driver-pay`, { driver_pay: parseFloat(driverPay) });
      toast.success("Driver pay updated successfully");
      // Update local booking state
      setBooking(prev => ({ ...prev, driver_pay: parseFloat(driverPay) }));
    } catch (error) {
      toast.error("Failed to update driver pay");
    } finally {
      setSavingDriverPay(false);
    }
  };

  const handleSavePrice = async () => {
    if (!newPrice || isNaN(parseFloat(newPrice))) {
      toast.error("Please enter a valid price");
      return;
    }
    
    setSavingPrice(true);
    try {
      await api.put(`/bookings/${id}`, { price: parseFloat(newPrice) });
      toast.success("Booking price updated successfully");
      setBooking(prev => ({ ...prev, price: parseFloat(newPrice) }));
      setEditingPrice(false);
      // Refresh payment quote if exists
      fetchPaymentQuote();
    } catch (error) {
      toast.error("Failed to update price");
    } finally {
      setSavingPrice(false);
    }
  };

  const goBack = () => {
    // Navigate back to the bookings tab on admin/staff dashboard
    const dashboardRoutes = {
      admin: "/admin?tab=bookings",
      staff: "/staff?tab=bookings",
      client: "/client",
      driver: "/driver"
    };
    navigate(dashboardRoutes[user?.role] || "/dashboard");
  };

  // Effects - placed after function definitions to satisfy lint rules
  useEffect(() => {
    fetchBooking();
    fetchVehicleTypes();
    if (user?.role === "admin" || user?.role === "staff") {
      fetchDrivers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  useEffect(() => {
    // Fetch payment quote when booking is loaded and not paid
    if (booking && booking.payment_status !== "paid") {
      fetchPaymentQuote();
    }
    // Check for existing rating on completed bookings
    if (booking && booking.status === "completed") {
      checkExistingRating();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id, booking?.status]);

  const handlePayment = async () => {
    setProcessingPayment(true);
    try {
      const response = await api.post("/payments/checkout", {
        booking_id: id,
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to initiate payment");
      setProcessingPayment(false);
    }
  };

  const handleSendTracking = async () => {
    setSendingTracking(true);
    try {
      const response = await api.post(`/bookings/${id}/send-tracking`);
      toast.success("Tracking link sent to passenger!");
    } catch (error) {
      toast.error("Failed to send tracking link");
    } finally {
      setSendingTracking(false);
    }
  };

  const handleSendConfirmation = async () => {
    setSendingEmail(true);
    try {
      await api.post(`/bookings/${id}/send-confirmation`);
      toast.success("Confirmation email sent to passenger!");
    } catch (error) {
      toast.error("Failed to send confirmation email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleConfirm = async () => {
    try {
      await api.post(`/bookings/${id}/confirm`);
      toast.success("Booking confirmed");
      fetchBooking();
    } catch (error) {
      toast.error("Failed to confirm booking");
    }
  };

  const handleDispatch = async (driverId) => {
    try {
      const wasAlreadyDispatched = booking?.driver_id;
      await api.post(`/bookings/${id}/dispatch`, { driver_id: driverId, send_to_calendar: false });
      toast.success(wasAlreadyDispatched ? "Driver reassigned successfully" : "Booking dispatched");
      fetchBooking();
    } catch (error) {
      toast.error(booking?.driver_id ? "Failed to reassign driver" : "Failed to dispatch booking");
    }
  };

  const handleComplete = async () => {
    try {
      await api.post(`/bookings/${id}/complete`);
      toast.success("Booking completed");
      fetchBooking();
    } catch (error) {
      toast.error("Failed to complete booking");
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) return;
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      fetchBooking();
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  // Open edit dialog with current booking data
  const handleOpenEdit = () => {
    setEditForm({
      passenger_name: booking.passenger_name || "",
      passenger_email: booking.passenger_email || "",
      passenger_phone: booking.passenger_phone || "",
      pickup_location: booking.pickup_location || "",
      dropoff_location: booking.dropoff_location || "",
      pickup_datetime: booking.pickup_datetime ? booking.pickup_datetime.slice(0, 16) : "",
      flight_number: booking.flight_number || "",
      special_requirements: booking.special_requirements || "",
      notes: booking.notes || "",
      price: booking.price || 0,
      driver_pay: booking.driver_pay || 0,
      vehicle_type: booking.vehicle_type || "sedan",
      num_passengers: booking.num_passengers || 1,
      stops: booking.stops || []
    });
    setShowEditDialog(true);
  };

  // Save booking edits
  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      // Filter out empty stops
      const filteredStops = (editForm.stops || []).filter(s => s && s.trim() !== "");
      
      await api.put(`/bookings/${id}`, {
        ...editForm,
        price: parseFloat(editForm.price) || booking.price,
        driver_pay: parseFloat(editForm.driver_pay) || booking.driver_pay,
        stops: filteredStops.length > 0 ? filteredStops : null
      });
      toast.success("Booking updated successfully");
      setShowEditDialog(false);
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update booking");
    } finally {
      setSavingEdit(false);
    }
  };

  // Mark booking as paid without invoice
  const handleMarkAsPaid = async () => {
    if (!window.confirm("Mark this booking as paid? This will update the payment status without creating an invoice.")) return;
    setMarkingPaid(true);
    try {
      await api.patch(`/bookings/${id}/payment-status`, { payment_status: "paid" });
      toast.success("Booking marked as paid");
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setMarkingPaid(false);
    }
  };

  // Create invoice for this booking
  const handleCreateInvoice = async () => {
    setCreatingInvoice(true);
    try {
      // Get client info from booking
      const clientId = booking.client_id || booking.created_by_id;
      const clientName = booking.client_name || booking.passenger_name;
      const clientEmail = booking.passenger_email;
      
      const invoiceData = {
        client_id: clientId,
        client_name: clientName,
        client_email: clientEmail,
        booking_ids: [booking.id],
        notes: `Invoice for booking ${booking.booking_ref}`,
        payment_method: "bank_transfer"
      };
      
      const response = await api.post("/invoices", invoiceData);
      toast.success(`Invoice ${response.data.invoice_number} created`);
      navigate(`/invoices`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setCreatingInvoice(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "status-pending",
      confirmed: "status-confirmed",
      dispatched: "status-dispatched",
      in_progress: "status-in_progress",
      completed: "status-completed",
      cancelled: "status-cancelled"
    };
    return <Badge className={`${styles[status]} capitalize text-base px-3 py-1`}>{status.replace("_", " ")}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const styles = {
      pending: "payment-pending",
      paid: "payment-paid",
      failed: "payment-failed"
    };
    return <Badge className={`${styles[status]} capitalize`}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!booking) return null;

  const isAdmin = user?.role === "admin" || user?.role === "staff";
  const isDriver = user?.role === "driver";
  const canModify = booking.status !== "completed" && booking.status !== "cancelled";
  const canAdminEdit = isAdmin; // Admin can always see edit/action buttons

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <button onClick={goBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => copyToClipboard(booking.booking_ref)}
                className="flex items-center gap-2 text-primary hover:text-primary/80"
              >
                <span className="mono font-bold">{booking.booking_ref}</span>
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-20 pb-8 px-4 max-w-4xl mx-auto">
        {/* Status Header */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {getStatusBadge(booking.status)}
          {getPaymentBadge(booking.payment_status)}
        </div>

        <div className="grid gap-6">
          {/* Route Card */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Route
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">PICKUP</p>
                  <p className="font-medium">{booking.pickup_location}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">DROPOFF</p>
                  <p className="font-medium">{booking.dropoff_location}</p>
                </div>
              </div>
              
              {/* Display stops if any */}
              {booking.stops && booking.stops.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-xs text-muted-foreground mb-2">VIA STOPS</p>
                  <div className="space-y-2">
                    {booking.stops.map((stop, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs text-yellow-500">{idx + 1}</span>
                        </div>
                        <span className="text-sm">{stop}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex flex-wrap gap-4 pt-4 border-t border-white/5">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(booking.pickup_datetime).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>{new Date(booking.pickup_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Car className="h-4 w-4" />
                  <span className="capitalize">{booking.vehicle_type}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Passenger Card */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Passenger
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-lg">{booking.passenger_name}</p>
                  <p className="text-sm text-muted-foreground">{booking.num_passengers} passenger{booking.num_passengers > 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${booking.passenger_email}`} className="text-sm hover:text-primary">{booking.passenger_email}</a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${booking.passenger_phone}`} className="text-sm hover:text-primary">{booking.passenger_phone}</a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Flight Info */}
          {booking.flight_number && (
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plane className="h-5 w-5 text-primary" />
                  Flight Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground">FLIGHT NUMBER</p>
                    <p className="font-medium mono">{booking.flight_number}</p>
                  </div>
                  {booking.flight_arrival_time && (
                    <div>
                      <p className="text-xs text-muted-foreground">ARRIVAL TIME</p>
                      <p className="font-medium">{booking.flight_arrival_time}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Special Requirements */}
          {booking.special_requirements && (
            <Card className="dashboard-card bg-yellow-500/5 border-yellow-500/20">
              <CardContent className="py-4">
                <p className="text-xs text-yellow-500 font-medium mb-1">SPECIAL REQUIREMENTS</p>
                <p>{booking.special_requirements}</p>
              </CardContent>
            </Card>
          )}

          {/* Driver Info */}
          {booking.driver_name && (
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5 text-primary" />
                  Assigned Driver
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <User className="h-6 w-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="font-medium">{booking.driver_name}</p>
                    <p className="text-sm text-muted-foreground">Driver</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Driver Pay Card - Admin/Staff Only - Available at any time before payment */}
          {(user?.role === "admin" || user?.role === "staff") && booking.payment_status !== "paid" && (
            <Card className="dashboard-card" data-testid="driver-pay-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-primary" />
                  Driver Pay
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                    <span className="text-sm text-muted-foreground">Customer Fare:</span>
                    <span className="font-medium">${(booking.price || 0).toFixed(2)}</span>
                  </div>
                  <div className="space-y-2">
                    <Label>Driver Pay Amount ($)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={driverPay}
                        onChange={(e) => setDriverPay(e.target.value)}
                        placeholder="Enter driver pay"
                        data-testid="driver-pay-input"
                      />
                      <Button 
                        onClick={handleSaveDriverPay}
                        disabled={savingDriverPay}
                        data-testid="save-driver-pay-btn"
                      >
                        {savingDriverPay ? "Saving..." : "Save"}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Set the amount to pay the driver for this job. Can be updated until payment is made.
                    </p>
                  </div>
                  {booking.driver_pay !== null && booking.driver_pay !== undefined && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                      <span className="text-sm text-green-500">Current Driver Pay:</span>
                      <span className="font-bold text-green-500">${(booking.driver_pay || 0).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Card */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                Payment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Booking Amount</p>
                  {editingPrice ? (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xl font-bold text-primary">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                        className="w-32 h-10 text-xl font-bold bg-secondary/50 border-black/10"
                        placeholder={(booking.price || 0).toFixed(2)}
                        data-testid="edit-price-input"
                      />
                      <Button 
                        size="sm" 
                        onClick={handleSavePrice}
                        disabled={savingPrice}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="save-price-btn"
                      >
                        {savingPrice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => { setEditingPrice(false); setNewPrice(""); }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="text-3xl font-bold text-primary">${(booking.price || 0).toFixed(2)} AUD</p>
                      {user?.role === "admin" && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => { setEditingPrice(true); setNewPrice((booking.price || 0).toFixed(2)); }}
                          className="text-muted-foreground hover:text-primary"
                          data-testid="edit-price-btn"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  {getPaymentBadge(booking.payment_status)}
                </div>
              </div>
              
              {/* Payment Method Display */}
              {booking.payment_method && (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Payment Method:</span>
                  <Badge variant="outline" className={
                    booking.payment_method === "credit_card" ? "border-blue-500/50 text-blue-400" :
                    booking.payment_method === "invoice" ? "border-orange-500/50 text-orange-400" :
                    booking.payment_method === "cash" ? "border-green-500/50 text-green-400" :
                    "border-purple-500/50 text-purple-400"
                  }>
                    {booking.payment_method === "credit_card" && "Credit Card"}
                    {booking.payment_method === "invoice" && "Invoice"}
                    {booking.payment_method === "cash" && "Cash"}
                    {booking.payment_method === "bank_transfer" && "Bank Transfer"}
                  </Badge>
                </div>
              )}
              
              {/* Admin Note for Price Editing */}
              {user?.role === "admin" && !editingPrice && (
                <p className="text-xs text-muted-foreground mt-2">
                  Click the edit icon to adjust the booking price at any time
                </p>
              )}
              
              {/* Corporate Discount Breakdown */}
              {booking.discount_percentage > 0 && (
                <div className="mt-4 p-4 bg-green-500/10 rounded-lg border border-green-500/20" data-testid="discount-breakdown">
                  <p className="text-xs text-green-500 font-medium mb-2">CORPORATE DISCOUNT APPLIED</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Original Price:</span>
                      <span className="line-through text-muted-foreground">${booking.original_price?.toFixed(2) || (booking.price || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-green-500">Discount ({booking.discount_percentage}%):</span>
                      <span className="text-green-500">-${booking.discount_amount?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between font-bold pt-2 border-t border-green-500/20">
                      <span>Final Price:</span>
                      <span className="text-primary">${(booking.price || 0).toFixed(2)}</span>
                    </div>
                  </div>
                  {booking.corporate_client_name && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Corporate Account: {booking.corporate_client_name}
                    </p>
                  )}
                </div>
              )}
              
              {/* Payment with Card Fee Breakdown */}
              {booking.payment_status === "pending" && booking.price > 0 && paymentQuote && (
                <div className="mt-4 p-4 bg-background/50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Booking Amount:</span>
                    <span>${(paymentQuote.booking_price || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Card Processing Fee ({paymentQuote.card_fee_percentage}%):</span>
                    <span>${(paymentQuote.card_fee || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t border-black/10">
                    <span>Total to Pay:</span>
                    <span className="text-primary">${(paymentQuote.total_amount || 0).toFixed(2)} {paymentQuote.currency}</span>
                  </div>
                </div>
              )}
              
              {booking.payment_status === "pending" && booking.price > 0 && user?.role === "client" && canModify && (
                <Button 
                  className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white rounded-full"
                  onClick={handlePayment}
                  disabled={processingPayment}
                  data-testid="pay-now-btn"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay ${paymentQuote?.total_amount?.toFixed(2) || (booking.price || 0).toFixed(2)} AUD
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Driver Location Tracking (for clients) */}
          {user?.role === "client" && (booking.status === "dispatched" || booking.status === "in_progress") && booking.driver_id && (
            <DriverLocationTracker bookingId={id} />
          )}

          {/* Rating Section (for completed bookings) */}
          {booking.status === "completed" && user?.role === "client" && (
            <Card className="dashboard-card" data-testid="rating-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500" />
                  Rate Your Experience
                </CardTitle>
              </CardHeader>
              <CardContent>
                {existingRating ? (
                  <div className="text-center py-4">
                    <div className="flex justify-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star 
                          key={star}
                          className={`h-8 w-8 ${existingRating.rating >= star ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}`}
                        />
                      ))}
                    </div>
                    <p className="text-muted-foreground">Thank you for your feedback!</p>
                    {existingRating.feedback && (
                      <p className="mt-2 text-sm italic">&ldquo;{existingRating.feedback}&rdquo;</p>
                    )}
                  </div>
                ) : showRatingForm ? (
                  <RatingSubmitForm 
                    bookingId={id} 
                    onSuccess={() => {
                      setShowRatingForm(false);
                      checkExistingRating();
                    }} 
                  />
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground mb-4">How was your ride with Limo Near Me?</p>
                    <Button onClick={() => setShowRatingForm(true)} data-testid="open-rating-btn">
                      <Star className="h-4 w-4 mr-2" />
                      Leave a Review
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          {(canModify || canAdminEdit) && (
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {/* Send Confirmation Email */}
                  {isAdmin && (
                    <Button 
                      onClick={handleSendConfirmation}
                      disabled={sendingEmail}
                      variant="outline"
                      data-testid="send-confirmation-btn"
                    >
                      {sendingEmail ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <MailCheck className="h-4 w-4 mr-2" />
                      )}
                      Send Confirmation Email
                    </Button>
                  )}

                  {/* Send Tracking Link */}
                  {isAdmin && (booking.status === "dispatched" || booking.status === "in_progress") && (
                    <Button 
                      onClick={handleSendTracking}
                      disabled={sendingTracking}
                      variant="outline"
                      data-testid="send-tracking-btn"
                    >
                      {sendingTracking ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Navigation className="h-4 w-4 mr-2" />
                      )}
                      Send Tracking Link
                    </Button>
                  )}
                  
                  {isAdmin && booking.status === "pending" && (
                    <Button onClick={handleConfirm} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-btn">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Confirm Booking
                    </Button>
                  )}
                  
                  {isAdmin && (booking.status === "confirmed" || booking.status === "pending") && !booking.driver_id && drivers.length > 0 && (
                    <Select onValueChange={handleDispatch}>
                      <SelectTrigger className="w-48" data-testid="dispatch-select">
                        <Truck className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Dispatch to driver" />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.map(driver => (
                          <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {isAdmin && booking.status === "dispatched" && drivers.length > 0 && (
                    <Select onValueChange={handleDispatch}>
                      <SelectTrigger className="w-48" data-testid="reassign-select">
                        <Truck className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Reassign driver" />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.filter(d => d.id !== booking.driver_id).map(driver => (
                          <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {(isAdmin || (isDriver && booking.driver_id === user?.id)) && booking.status === "dispatched" && (
                    <Button onClick={handleComplete} className="bg-green-600 hover:bg-green-700" data-testid="complete-btn">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark Complete
                    </Button>
                  )}
                  
                  {(isAdmin || (user?.role === "client" && booking.status === "pending")) && (
                    <Button variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={handleCancel} data-testid="cancel-btn">
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancel Booking
                    </Button>
                  )}
                  
                  {/* Edit Booking - Always available for Admin */}
                  {isAdmin && (
                    <Button variant="outline" onClick={handleOpenEdit} data-testid="edit-booking-btn">
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit Booking
                    </Button>
                  )}
                  
                  {/* Mark as Paid - For unpaid bookings */}
                  {isAdmin && booking.payment_status !== "paid" && (
                    <Button 
                      variant="outline" 
                      className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                      onClick={handleMarkAsPaid}
                      disabled={markingPaid}
                      data-testid="mark-paid-btn"
                    >
                      {markingPaid ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Banknote className="h-4 w-4 mr-2" />}
                      Mark as Paid
                    </Button>
                  )}
                  
                  {/* Create Invoice - Available for all bookings except cancelled */}
                  {isAdmin && booking.status !== "cancelled" && (
                    <Button 
                      variant="outline"
                      onClick={handleCreateInvoice}
                      disabled={creatingInvoice}
                      data-testid="create-invoice-btn"
                    >
                      {creatingInvoice ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                      Create Invoice
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Booking Meta */}
          <div className="text-center text-sm text-muted-foreground">
            <p>Created by {booking.created_by_name} on {new Date(booking.created_at).toLocaleString()}</p>
            {booking.updated_at !== booking.created_at && (
              <p>Last updated: {new Date(booking.updated_at).toLocaleString()}</p>
            )}
          </div>
        </div>
      </main>

      {/* Edit Booking Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-primary" />
              Edit Booking - {booking?.booking_ref}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Passenger Name</Label>
              <Input
                value={editForm.passenger_name || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, passenger_name: e.target.value }))}
                data-testid="edit-passenger-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Passenger Phone</Label>
              <Input
                value={editForm.passenger_phone || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, passenger_phone: e.target.value }))}
                data-testid="edit-passenger-phone"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Passenger Email</Label>
              <Input
                type="email"
                value={editForm.passenger_email || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, passenger_email: e.target.value }))}
                data-testid="edit-passenger-email"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Pickup Location</Label>
              <Input
                value={editForm.pickup_location || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, pickup_location: e.target.value }))}
                data-testid="edit-pickup-location"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Drop-off Location</Label>
              <Input
                value={editForm.dropoff_location || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, dropoff_location: e.target.value }))}
                data-testid="edit-dropoff-location"
              />
            </div>
            <div className="space-y-2">
              <Label>Pickup Date & Time</Label>
              <Input
                type="datetime-local"
                value={editForm.pickup_datetime || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, pickup_datetime: e.target.value }))}
                data-testid="edit-pickup-datetime"
              />
            </div>
            <div className="space-y-2">
              <Label>Vehicle Type</Label>
              <Select
                value={editForm.vehicle_type || "sedan"}
                onValueChange={(value) => setEditForm(prev => ({ ...prev, vehicle_type: value }))}
              >
                <SelectTrigger data-testid="edit-vehicle-type">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicleTypes.length > 0 ? (
                    vehicleTypes.map((v) => (
                      <SelectItem key={v.slug} value={v.slug}>
                        {v.name}
                      </SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="sedan">Sedan</SelectItem>
                      <SelectItem value="suv">SUV</SelectItem>
                      <SelectItem value="van">Van</SelectItem>
                      <SelectItem value="luxury">Luxury</SelectItem>
                      <SelectItem value="minibus">Minibus</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Number of Passengers</Label>
              <Input
                type="number"
                min="1"
                value={editForm.num_passengers || 1}
                onChange={(e) => setEditForm(prev => ({ ...prev, num_passengers: parseInt(e.target.value) || 1 }))}
                data-testid="edit-num-passengers"
              />
            </div>
            <div className="space-y-2">
              <Label>Flight Number</Label>
              <Input
                value={editForm.flight_number || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, flight_number: e.target.value }))}
                data-testid="edit-flight-number"
              />
            </div>
            <div className="space-y-2">
              <Label>Price ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={editForm.price || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                data-testid="edit-price"
              />
            </div>
            <div className="space-y-2">
              <Label>Driver Pay ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={editForm.driver_pay || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, driver_pay: e.target.value }))}
                data-testid="edit-driver-pay"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Special Requirements</Label>
              <Textarea
                value={editForm.special_requirements || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, special_requirements: e.target.value }))}
                rows={2}
                data-testid="edit-special-requirements"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={editForm.notes || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                data-testid="edit-notes"
              />
            </div>
            
            {/* Stops Section */}
            <div className="space-y-2 md:col-span-2">
              <div className="flex items-center justify-between">
                <Label>Stops (Via Points)</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setEditForm(prev => ({ 
                    ...prev, 
                    stops: [...(prev.stops || []), ""] 
                  }))}
                >
                  + Add Stop
                </Button>
              </div>
              {(editForm.stops || []).map((stop, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={stop}
                    placeholder={`Stop ${index + 1} address`}
                    onChange={(e) => {
                      const newStops = [...(editForm.stops || [])];
                      newStops[index] = e.target.value;
                      setEditForm(prev => ({ ...prev, stops: newStops }));
                    }}
                    data-testid={`edit-stop-${index}`}
                  />
                  <Button 
                    type="button" 
                    variant="destructive" 
                    size="icon"
                    onClick={() => {
                      const newStops = (editForm.stops || []).filter((_, i) => i !== index);
                      setEditForm(prev => ({ ...prev, stops: newStops }));
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {(!editForm.stops || editForm.stops.length === 0) && (
                <p className="text-sm text-muted-foreground">No stops added. Click Add Stop to include via points.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingDetail;
